import { createSlice } from '@reduxjs/toolkit';
import { fetchCreditBalance } from '../ActionTypes/creditBalanceActionTypes';
import { ErrorType, SerializableError } from '../../utils/errorHandling';

export interface CreditBalanceState {
  loading: boolean;
  totalCredits: number;
  usedCredits: number;
  remainingCredits: number;
  oneTimeCreditsPurchased: number;
  oneTimeCreditsUsed: number;
  oneTimeCreditsRemaining: number;
  lastRenewalDate: string | null;
  nextRenewalDate: string | null;
  warning: string | null;
  error: SerializableError | null;
}

interface CreditBalanceFromBe {
  last_renewal_date: string;
  next_renewal_date: string;
  granted_credits: number;
  granted_credits_details: {
    base?: number;
    addon?: number;
    unknown?: number;
  };
  consumed_credits: number;
  remaining_credits: number;
  one_time_credits_purchased?: number;
  one_time_credits_used?: number;
  one_time_credits_remaining?: number;
}

const initialState: CreditBalanceState = {
  loading: true,
  totalCredits: 0,
  usedCredits: 0,
  remainingCredits: 0,
  oneTimeCreditsPurchased: 0,
  oneTimeCreditsUsed: 0,
  oneTimeCreditsRemaining: 0,
  lastRenewalDate: null,
  nextRenewalDate: null,
  warning: null,
  error: null,
};

const creditBalanceSlice = createSlice({
  name: 'creditBalance',
  initialState,
  reducers: {
    resetCreditBalance: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCreditBalance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCreditBalance.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const balanceInfo = action.payload.data as CreditBalanceFromBe;
        const _totalCredits = balanceInfo.granted_credits;
        const _usedCredits = balanceInfo.consumed_credits;
        const _remainingCredits = balanceInfo.remaining_credits;

        // One-time credits: use explicit backend fields when available,
        // otherwise derive from the difference (remaining includes one-time
        // credits but granted does not).
        const _oneTimeRemaining =
          balanceInfo.one_time_credits_remaining ??
          Math.max(0, _remainingCredits - (_totalCredits - _usedCredits));
        const _oneTimePurchased =
          balanceInfo.one_time_credits_purchased ?? _oneTimeRemaining;
        const _oneTimeUsed = balanceInfo.one_time_credits_used ?? 0;

        let warningStr = '';
        if (_remainingCredits <= 0) {
          warningStr = 'You have no credits left.';
        } else if (
          _totalCredits > 0 &&
          _remainingCredits / _totalCredits < 0.1
        ) {
          warningStr = 'You are running out of credits.';
        }
        state.totalCredits = _totalCredits;
        state.usedCredits = _usedCredits;
        state.remainingCredits = _remainingCredits;
        state.oneTimeCreditsPurchased = _oneTimePurchased;
        state.oneTimeCreditsUsed = _oneTimeUsed;
        state.oneTimeCreditsRemaining = _oneTimeRemaining;

        state.lastRenewalDate = balanceInfo.last_renewal_date || null;
        state.nextRenewalDate = balanceInfo.next_renewal_date || null;
        state.warning = warningStr || null;
      })
      .addCase(fetchCreditBalance.rejected, (state, action) => {
        state.loading = false;
        const payload = (action.payload || {}) as {
          message?: string;
          statusCode?: number;
          errorCode?: string;
        };
        const isNetwork =
          payload.errorCode === 'NETWORK_ERROR' || payload.statusCode === 0;
        state.error = {
          message:
            payload.message ||
            'Failed to load credit balance. Please try again.',
          type: isNetwork ? ErrorType.NETWORK : ErrorType.UNKNOWN,
          statusCode: payload.statusCode,
          errorCode: payload.errorCode,
          isNetworkError: isNetwork,
        };
        console.warn('Credit balance fetch failed:', {
          statusCode: payload.statusCode,
          errorCode: payload.errorCode,
          message: payload.message,
        });
      });
  },
});

export const { resetCreditBalance } = creditBalanceSlice.actions;
export default creditBalanceSlice.reducer;
